/**
 * Print-Only Profit Gap Details Page for PDF Generation
 * Renders comprehensive profit gap analysis for PDF export
 *
 * URL: /print/profit-gap-details/:businessId
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';
import { useDateFormatter } from '../../utils/dateUtils';
import '../../print.css';

const PrintProfitGapDetails = () => {
  const { businessId } = useParams();
  const { formatDate } = useDateFormatter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    businessProfile: null,
    profitGap: null,
    valueGap: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };


        // Fetch data
        const [profileResponse, profitGapResponse, valueGapResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/profit-gap`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/value-gap`, { headers })
        ]);

        const profileData = profileResponse.ok ? await profileResponse.json() : null;
        const profitGapData = profitGapResponse.ok ? await profitGapResponse.json() : null;
        const valueGapData = valueGapResponse.ok ? await valueGapResponse.json() : null;

        setData({
          businessProfile: profileData?.data?.profile || null,
          profitGap: profitGapData?.profit_gap || null,
          valueGap: valueGapData?.value_gap || null
        });

        setLoading(false);
      } catch (err) {
        console.error('[PrintProfitGapDetails] Error loading data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  useEffect(() => {
    if (!loading && !error) {
      document.body.setAttribute('data-pdf-ready', 'true');
    
      // Set document title to business name
      document.title = `Profit Gap Analysis Report`;
    }
  }, [loading, error]);

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading profit gap details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const businessName = data?.businessProfile?.business_name || 'Business Name Not Set';
  const industry = data?.businessProfile?.industry;
  const profitGap = data?.profitGap;
  const valueGap = data?.valueGap;

  if (!profitGap) {
    return (
      <div className="print-container" data-pdf-ready="true">
        <div className="print-page">No profit gap data available</div>
      </div>
    );
  }

  // Calculate valuation matrix for all combinations (from ValuationProgressionModal)
  const calculateMatrix = () => {
    if (!valueGap || !profitGap) return null;

    const revenue = profitGap.current_revenue || 0;
    const currentEbitda = profitGap.current_ebitda || 1;
    const currentMargin = profitGap.current_ebitda_margin || 0;
    const medianMargin = profitGap.industry_median_ebitda_margin || 0;
    const bestMargin = profitGap.industry_high_ebitda_margin || 0;
    const ebitdaMultiple = profitGap.ebitda_multiple || 0;

    const currentScore = valueGap.current_attractiveness || 58;
    const greenZoneScore = 67;
    const bestScore = 72;

    // Get valuation range (these are dollar amounts at current EBITDA)
    const lowRange = valueGap.valuation_low || 0;
    const highRange = valueGap.valuation_high || 0;

    // Function to calculate valuation for a given EBITDA margin and assessment score
    const calculateValuation = (ebitdaMargin, assessmentScore) => {
      const newEbitda = revenue * (ebitdaMargin / 100);
      const scalingFactor = currentEbitda > 0 ? newEbitda / currentEbitda : 1;
      const newValLow = lowRange * scalingFactor;
      const newValHigh = highRange * scalingFactor;
      const newRange = newValHigh - newValLow;
      const position = Math.max(0, Math.min(100, (assessmentScore - 17) / 83 * 100));
      return newValLow + (newRange * position / 100);
    };

    // Calculate valuation ranges for each EBITDA margin scenario
    const calculateRangeForMargin = (ebitdaMargin) => {
      const newEbitda = revenue * (ebitdaMargin / 100);
      const scalingFactor = currentEbitda > 0 ? newEbitda / currentEbitda : 1;
      const newValLow = lowRange * scalingFactor;
      const newValHigh = highRange * scalingFactor;
      return { low: newValLow, high: newValHigh };
    };

    const currentMarginRange = calculateRangeForMargin(currentMargin);
    const medianMarginRange = calculateRangeForMargin(medianMargin);
    const bestMarginRange = calculateRangeForMargin(bestMargin);

    // Calculate all 9 matrix cells
    return {
      currentScore_currentMargin: calculateValuation(currentMargin, currentScore),
      currentScore_medianMargin: calculateValuation(medianMargin, currentScore),
      currentScore_bestMargin: calculateValuation(bestMargin, currentScore),
      greenZone_currentMargin: calculateValuation(currentMargin, greenZoneScore),
      greenZone_medianMargin: calculateValuation(medianMargin, greenZoneScore),
      greenZone_bestMargin: calculateValuation(bestMargin, greenZoneScore),
      bestScore_currentMargin: calculateValuation(currentMargin, bestScore),
      bestScore_medianMargin: calculateValuation(medianMargin, bestScore),
      bestScore_bestMargin: calculateValuation(bestMargin, bestScore),
      currentScore,
      greenZoneScore,
      bestScore,
      currentMargin,
      medianMargin,
      bestMargin,
      ebitdaMultiple,
      currentMarginRange,
      medianMarginRange,
      bestMarginRange
    };
  };

  const matrix = calculateMatrix();

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Profit Gap Analysis Report</h1>
          <div className="report-type">COMPREHENSIVE FINANCIAL EXIT PLANNING</div>
        </div>

        <div className="cover-business-info">
          <h2>{businessName}</h2>
          {industry && <p className="industry">{industry}</p>}
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Profit Gap Analysis Report</p>
        </div>
      </div>

      {/* Page 2: Summary, Industry Benchmark, Current Performance */}
      <div className="print-page" style={{ pageBreakAfter: 'always' }}>
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ paddingTop: '0.2in', paddingLeft: '0.5in', paddingRight: '0.5in' }}>
          {/* Summary */}
          <div style={{ marginBottom: '0.3in' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px', borderBottom: '3px solid #9333ea', paddingBottom: '8px' }}>Summary</h2>
            <div style={{ backgroundColor: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '16px' }}>
              <p style={{ color: '#374151', fontSize: '14px', marginBottom: '16px', lineHeight: '1.6' }}>
                Your profit gap measures the additional business value you could unlock by improving your EBITDA margin to industry benchmark levels.
              </p>

              {/* Two scenarios side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Median Industry Margin */}
                <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #bfdbfe' }}>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#1e3a8a', marginBottom: '8px' }}>At Median Industry Margin</p>
                  <p style={{ fontSize: '11px', color: '#1e40af', marginBottom: '12px' }}>({profitGap.industry_median_ebitda_margin.toFixed(1)}% EBITDA margin)</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#1e40af' }}>Valuation Impact:</span>
                    <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#2563eb' }}>
                      {formatCurrency(profitGap.valuation_impact_median)}
                    </span>
                  </div>
                </div>

                {/* Best in Class Margin */}
                <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #86efac' }}>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#14532d', marginBottom: '8px' }}>At Best in Class Margin</p>
                  <p style={{ fontSize: '11px', color: '#15803d', marginBottom: '12px' }}>({profitGap.industry_high_ebitda_margin.toFixed(1)}% EBITDA margin)</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#15803d' }}>Valuation Impact:</span>
                    <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#16a34a' }}>
                      {formatCurrency(profitGap.valuation_impact_best)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Industry Benchmark */}
          <div style={{ marginBottom: '0.3in' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px', borderBottom: '3px solid #9333ea', paddingBottom: '8px' }}>Industry Benchmark</h2>
            <div style={{ backgroundColor: '#eef2ff', borderRadius: '8px', padding: '16px', border: '1px solid #c7d2fe' }}>
              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '12px', color: '#4b5563', marginBottom: '4px' }}>Industry:</p>
                <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#312e81' }}>{profitGap.industry_name || 'Not specified'}</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', paddingTop: '12px', borderTop: '1px solid #c7d2fe' }}>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Your EBITDA Margin</p>
                  <p style={{ fontSize: '22px', fontWeight: 'bold', color: '#d97706' }}>{profitGap.current_ebitda_margin.toFixed(1)}%</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Median Industry Margin</p>
                  <p style={{ fontSize: '22px', fontWeight: 'bold', color: '#2563eb' }}>{profitGap.industry_median_ebitda_margin.toFixed(1)}%</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Best in Class Margin</p>
                  <p style={{ fontSize: '22px', fontWeight: 'bold', color: '#059669' }}>{profitGap.industry_high_ebitda_margin.toFixed(1)}%</p>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #c7d2fe' }}>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Gap to Median</p>
                  <p style={{ fontSize: '15px', fontWeight: 'bold', color: '#2563eb' }}>
                    {(profitGap.industry_median_ebitda_margin - profitGap.current_ebitda_margin).toFixed(1)} percentage points
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Gap to Best in Class</p>
                  <p style={{ fontSize: '15px', fontWeight: 'bold', color: '#16a34a' }}>
                    {(profitGap.industry_high_ebitda_margin - profitGap.current_ebitda_margin).toFixed(1)} percentage points
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Current Performance */}
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px', borderBottom: '3px solid #9333ea', paddingBottom: '8px' }}>Current Performance</h2>
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#4b5563' }}>Revenue:</span>
                <span style={{ fontWeight: '600', fontSize: '13px' }}>{formatCurrency(profitGap.current_revenue)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#4b5563' }}>Current EBITDA:</span>
                <span style={{ fontWeight: '600', fontSize: '13px' }}>{formatCurrency(profitGap.current_ebitda)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontSize: '13px', color: '#4b5563' }}>Current EBITDA Margin:</span>
                <span style={{ fontWeight: '600', fontSize: '13px', color: '#d97706' }}>{profitGap.current_ebitda_margin.toFixed(1)}%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #d1d5db', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#4b5563' }}>Target EBITDA (at median margin):</span>
                <span style={{ fontWeight: '600', fontSize: '13px', color: '#2563eb' }}>{formatCurrency(profitGap.target_ebitda_median)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: '#4b5563' }}>Target EBITDA (at best in class):</span>
                <span style={{ fontWeight: '600', fontSize: '13px', color: '#16a34a' }}>{formatCurrency(profitGap.target_ebitda_best)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page 3: Step-by-Step Calculations (Side by Side) */}
      <div className="print-page" style={{ pageBreakAfter: 'always' }}>
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ paddingTop: '0.15in', paddingLeft: '0.4in', paddingRight: '0.4in' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '10px', borderBottom: '3px solid #9333ea', paddingBottom: '6px' }}>Step-by-Step Calculations (Value Gap Framework)</h2>

          {/* Common Step: Position Calculation */}
          <div style={{ marginBottom: '10px', backgroundColor: '#faf5ff', border: '2px solid #d8b4fe', borderRadius: '6px', padding: '10px' }}>
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#581c87', marginBottom: '4px' }}>Assessment Score Position</p>
            <p style={{ fontSize: '12px', color: '#6b21a8' }}>
              Position = (Assessment Score - 17) / 83 × 100 = ({profitGap.assessment_score?.toFixed(1)}% - 17) / 83 × 100 = <strong>{(((profitGap.assessment_score - 17) / 83) * 100).toFixed(1)}%</strong>
            </p>
          </div>

          {/* Side by Side Scenarios */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* Scenario 1: Median */}
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e3a8a', marginBottom: '8px' }}>Scenario 1: At Median ({profitGap.industry_median_ebitda_margin.toFixed(1)}%)</h3>

              {/* Step 1 */}
              <div style={{ backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', padding: '8px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a8a', marginBottom: '2px' }}>Step 1: New EBITDA</p>
                <p style={{ fontSize: '11px', color: '#1e40af' }}>
                  = {formatCurrency(profitGap.current_revenue)} × {profitGap.industry_median_ebitda_margin.toFixed(1)}%
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e3a8a', marginTop: '2px' }}>
                  = {formatCurrency(profitGap.target_ebitda_median)}
                </p>
              </div>

              {/* Step 2 */}
              <div style={{ backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', padding: '8px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a8a', marginBottom: '2px' }}>Step 2: Scaling Factor</p>
                <p style={{ fontSize: '11px', color: '#1e40af' }}>
                  = {formatCurrency(profitGap.target_ebitda_median)} / {formatCurrency(profitGap.current_ebitda)}
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e3a8a', marginTop: '2px' }}>
                  = {(profitGap.target_ebitda_median / profitGap.current_ebitda).toFixed(3)}
                </p>
              </div>

              {/* Step 3 */}
              <div style={{ backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', padding: '8px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a8a', marginBottom: '2px' }}>Step 3: Scale Valuation Range</p>
                <p style={{ fontSize: '11px', color: '#1e40af' }}>
                  New Low = {formatCurrency(profitGap.valuation_low)} × {(profitGap.target_ebitda_median / profitGap.current_ebitda).toFixed(3)}
                </p>
                <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#1e3a8a' }}>
                  = {formatCurrency(profitGap.valuation_low * (profitGap.target_ebitda_median / profitGap.current_ebitda))}
                </p>
                <p style={{ fontSize: '11px', color: '#1e40af', marginTop: '4px' }}>
                  New High = {formatCurrency(profitGap.valuation_high)} × {(profitGap.target_ebitda_median / profitGap.current_ebitda).toFixed(3)}
                </p>
                <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#1e3a8a' }}>
                  = {formatCurrency(profitGap.valuation_high * (profitGap.target_ebitda_median / profitGap.current_ebitda))}
                </p>
              </div>

              {/* Step 4 */}
              <div style={{ backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', padding: '8px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a8a', marginBottom: '2px' }}>Step 4: Valuation at Position</p>
                <p style={{ fontSize: '11px', color: '#1e40af' }}>
                  = New Low + (New Range × {(((profitGap.assessment_score - 17) / 83) * 100).toFixed(1)}%)
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e3a8a', marginTop: '2px' }}>
                  = {formatCurrency(profitGap.median_valuation)}
                </p>
              </div>

              {/* Step 5 */}
              <div style={{ backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', padding: '8px', borderRadius: '4px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a8a', marginBottom: '2px' }}>Step 5: Valuation Impact</p>
                <p style={{ fontSize: '11px', color: '#1e40af' }}>
                  = {formatCurrency(profitGap.median_valuation)} - {formatCurrency(profitGap.current_valuation)}
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e3a8a', marginTop: '2px' }}>
                  = {formatCurrency(profitGap.valuation_impact_median)}
                </p>
              </div>
            </div>

            {/* Scenario 2: Best in Class */}
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#14532d', marginBottom: '8px' }}>Scenario 2: At Best in Class ({profitGap.industry_high_ebitda_margin.toFixed(1)}%)</h3>

              {/* Step 1 */}
              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '3px solid #22c55e', padding: '8px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#14532d', marginBottom: '2px' }}>Step 1: New EBITDA</p>
                <p style={{ fontSize: '11px', color: '#15803d' }}>
                  = {formatCurrency(profitGap.current_revenue)} × {profitGap.industry_high_ebitda_margin.toFixed(1)}%
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#14532d', marginTop: '2px' }}>
                  = {formatCurrency(profitGap.target_ebitda_best)}
                </p>
              </div>

              {/* Step 2 */}
              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '3px solid #22c55e', padding: '8px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#14532d', marginBottom: '2px' }}>Step 2: Scaling Factor</p>
                <p style={{ fontSize: '11px', color: '#15803d' }}>
                  = {formatCurrency(profitGap.target_ebitda_best)} / {formatCurrency(profitGap.current_ebitda)}
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#14532d', marginTop: '2px' }}>
                  = {(profitGap.target_ebitda_best / profitGap.current_ebitda).toFixed(3)}
                </p>
              </div>

              {/* Step 3 */}
              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '3px solid #22c55e', padding: '8px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#14532d', marginBottom: '2px' }}>Step 3: Scale Valuation Range</p>
                <p style={{ fontSize: '11px', color: '#15803d' }}>
                  New Low = {formatCurrency(profitGap.valuation_low)} × {(profitGap.target_ebitda_best / profitGap.current_ebitda).toFixed(3)}
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#14532d' }}>
                  = {formatCurrency(profitGap.valuation_low * (profitGap.target_ebitda_best / profitGap.current_ebitda))}
                </p>
                <p style={{ fontSize: '11px', color: '#15803d', marginTop: '4px' }}>
                  New High = {formatCurrency(profitGap.valuation_high)} × {(profitGap.target_ebitda_best / profitGap.current_ebitda).toFixed(3)}
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#14532d' }}>
                  = {formatCurrency(profitGap.valuation_high * (profitGap.target_ebitda_best / profitGap.current_ebitda))}
                </p>
              </div>

              {/* Step 4 */}
              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '3px solid #22c55e', padding: '8px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#14532d', marginBottom: '2px' }}>Step 4: Valuation at Position</p>
                <p style={{ fontSize: '11px', color: '#15803d' }}>
                  = New Low + (New Range × {(((profitGap.assessment_score - 17) / 83) * 100).toFixed(1)}%)
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#14532d', marginTop: '2px' }}>
                  = {formatCurrency(profitGap.best_valuation)}
                </p>
              </div>

              {/* Step 5 */}
              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '3px solid #22c55e', padding: '8px', borderRadius: '4px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#14532d', marginBottom: '2px' }}>Step 5: Valuation Impact</p>
                <p style={{ fontSize: '11px', color: '#15803d' }}>
                  = {formatCurrency(profitGap.best_valuation)} - {formatCurrency(profitGap.current_valuation)}
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#14532d', marginTop: '2px' }}>
                  = {formatCurrency(profitGap.valuation_impact_best)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page 4: What This Means */}
      <div className="print-page" style={{ pageBreakAfter: 'always' }}>
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ paddingTop: '0.2in', paddingLeft: '0.5in', paddingRight: '0.5in' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px', borderBottom: '3px solid #9333ea', paddingBottom: '8px' }}>What This Means</h2>

          <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px' }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ padding: '16px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', marginBottom: '16px' }}>
                <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                  <span style={{ fontWeight: '600', color: '#1e3a8a' }}>Median Industry Margin Scenario:</span> By improving your EBITDA margin from <span style={{ fontWeight: '600' }}>{profitGap.current_ebitda_margin.toFixed(1)}%</span> to
                  the <span style={{ fontWeight: '600' }}>{profitGap.industry_name || 'industry'}</span> median of <span style={{ fontWeight: '600' }}>{profitGap.industry_median_ebitda_margin.toFixed(1)}%</span>,
                  you could add <span style={{ fontWeight: '600', color: '#2563eb' }}>{formatCurrency(profitGap.valuation_impact_median)}</span> to your business value.
                </p>
              </div>

              <div style={{ padding: '16px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
                <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                  <span style={{ fontWeight: '600', color: '#14532d' }}>Best in Class Scenario:</span> By improving your EBITDA margin to
                  the <span style={{ fontWeight: '600' }}>{profitGap.industry_name || 'industry'}</span> best in class of <span style={{ fontWeight: '600' }}>{profitGap.industry_high_ebitda_margin.toFixed(1)}%</span>,
                  you could add <span style={{ fontWeight: '600', color: '#16a34a' }}>{formatCurrency(profitGap.valuation_impact_best)}</span> to your business value.
                </p>
              </div>
            </div>

            <p style={{ fontSize: '14px', color: '#374151', marginTop: '20px', lineHeight: '1.6' }}>
              These calculations use the EBITDA multiple of <span style={{ fontWeight: '600' }}>{profitGap.ebitda_multiple}x</span> from your most recent valuation
              and apply a <span style={{ fontWeight: '600' }}>{(profitGap.private_company_discount * 100).toFixed(0)}%</span> private company discount.
            </p>
            <p style={{ fontSize: '14px', color: '#374151', marginTop: '16px', lineHeight: '1.6' }}>
              Focus on increasing profitability through operational efficiency, cost management, and revenue optimization. Even reaching the median margin ({profitGap.industry_median_ebitda_margin.toFixed(1)}%)
              represents a {(profitGap.industry_median_ebitda_margin - profitGap.current_ebitda_margin).toFixed(1)} percentage point improvement
              and significant value creation opportunity.
            </p>
          </div>
        </div>
      </div>

      {/* Page 5: Your Valuation Journey - Matrix */}
      {matrix && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <div style={{ paddingTop: '0.15in', paddingLeft: '0.4in', paddingRight: '0.4in' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px', borderBottom: '3px solid #9333ea', paddingBottom: '8px' }}>Your Valuation Journey</h2>

            {/* Valuation Scenarios Matrix */}
            <div style={{ marginBottom: '15px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', marginBottom: '4px', textAlign: 'center' }}>Valuation Scenarios Matrix</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ borderright: '2px solid #1f2937', borderbottom: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', width: '140px' }}></th>
                    <th colSpan="3" style={{ border: '2px solid #1f2937', backgroundColor: '#f3f4f6', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      EBITDA Margin
                    </th>
                  </tr>
                  <tr>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#f3f4f6', padding: '4px', textAlign: 'right', fontWeight: 'bold', width: '140px' }}>
                      Assessment Score
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Current
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Median
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Best In Class
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'right' }}>
                      <strong>Current</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.currentScore_currentMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.currentScore_medianMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.currentScore_bestMargin)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'right' }}>
                      <strong>Green Zone</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.greenZone_currentMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.greenZone_medianMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.greenZone_bestMargin)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'right' }}>
                      <strong>Best In Class</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.bestScore_currentMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.bestScore_medianMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.bestScore_bestMargin)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Change from Baseline Matrix */}
            <div style={{ marginBottom: '15px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', marginBottom: '4px', textAlign: 'center' }}>Change from Current Baseline</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ borderright: '2px solid #1f2937', borderbottom: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', width: '140px' }}></th>
                    <th colSpan="3" style={{ border: '2px solid #1f2937', backgroundColor: '#f3f4f6', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      EBITDA Margin
                    </th>
                  </tr>
                  <tr>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#f3f4f6', padding: '4px', textAlign: 'right', fontWeight: 'bold', width: '140px' }}>
                      Assessment Score
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Current
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Median
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Best In Class
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'right' }}>
                      <strong>Current</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>$0</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>(0%)</div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.currentScore_medianMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.currentScore_medianMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.currentScore_bestMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.currentScore_bestMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'right' }}>
                      <strong>Green Zone</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.greenZone_currentMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.greenZone_currentMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.greenZone_medianMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.greenZone_medianMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.greenZone_bestMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.greenZone_bestMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'right' }}>
                      <strong>Best In Class</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.bestScore_currentMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.bestScore_currentMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.bestScore_medianMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.bestScore_medianMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.bestScore_bestMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.bestScore_bestMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic', marginTop: '4px' }}>
                All changes are calculated relative to the Current/Current baseline value of {formatCurrency(matrix.currentScore_currentMargin)}
              </p>
            </div>

            {/* Matrix Parameters and Valuation Ranges */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '12px', color: '#4b5563' }}>
              {/* Matrix Parameters */}
              <div>
                <p style={{ marginBottom: '4px', fontSize: '15px', fontWeight: '600', borderBottom: '1px solid #4b5563' }}>Matrix Parameters:</p>
                <ul style={{ margin: 0, paddingLeft: '16px', lineHeight: '2' }}>
                  <li><strong>Industry EV/EBITDA Multiple:</strong> {matrix.ebitdaMultiple.toFixed(2)}x</li>
                  <li><strong>Current EBITDA Margin:</strong> {matrix.currentMargin.toFixed(1)}%</li>
                  <li><strong>Median Industry EBITDA Margin:</strong> {matrix.medianMargin.toFixed(1)}%</li>
                  <li><strong>Best In Class Industry EBITDA Margin:</strong> {matrix.bestMargin.toFixed(1)}%</li>
                  <li><strong>Current Overall Assessment Score:</strong> {matrix.currentScore.toFixed(0)}%</li>
                  <li><strong>Green Zone Assessment Score:</strong> {matrix.greenZoneScore}%</li>
                  <li><strong>Best In Class Assessment Score:</strong> {matrix.bestScore}%</li>
                </ul>
              </div>

              {/* Valuation Ranges */}
              <div>
                <p style={{ marginBottom: '4px', fontSize: '15px', fontWeight: '600', borderBottom: '1px solid #4b5563' }}>Valuation Ranges by EBITDA Margin:</p>
                <ul style={{ margin: 0, paddingLeft: '16px', lineHeight: '2' }}>
                  <li>
                    <strong>Current Margin ({matrix.currentMargin.toFixed(1)}%):</strong><br />
                    {formatCurrency(matrix.currentMarginRange.low)} - {formatCurrency(matrix.currentMarginRange.high)}
                  </li>
                  <li>
                    <strong>Median Margin ({matrix.medianMargin.toFixed(1)}%):</strong><br />
                    {formatCurrency(matrix.medianMarginRange.low)} - {formatCurrency(matrix.medianMarginRange.high)}
                  </li>
                  <li>
                    <strong>Best In Class Margin ({matrix.bestMargin.toFixed(1)}%):</strong><br />
                    {formatCurrency(matrix.bestMarginRange.low)} - {formatCurrency(matrix.bestMarginRange.high)}
                  </li>
                </ul>
              </div>
            </div>

            <p style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic', marginTop: '8px' }}>
              Calculations use Value Gap framework: Position = (Score - 17) / 83 × 100; Valuation = Low + (Range × Position)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintProfitGapDetails;
